import { makeStyles } from '@material-ui/styles'
import GetDialogButton from 'app/plugins/openstack/components/vms/vm-details/GetDailogButton'
import { ArrayElement } from 'core/actions/Action'
import { clientActions } from 'core/client/clientReducers'
import DocumentMeta from 'core/components/DocumentMeta'
import Button from 'core/elements/button/Button'
import { BatchActionDropdown } from 'core/elements/grid/GridToolbar'
import { GridBatchActionGroupProps } from 'core/elements/grid/hooks/useGridSelectableRows'
import { HeaderPrimaryActionPortal, HeaderRefreshPortal } from 'core/elements/header/portals'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { isReadonlyUser } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { getBlueprints } from 'openstack/components/infrastructure/actions'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { listSingleVolume, updateSingleVolume } from '../actions'
import AttachVolumeDialog from '../AttachVolumeDialog'
import CancelTransferDialog from '../CancelTransferDialog'
import DetachVolumeDialog from '../DetachVolumeDialog'
import EditVolumeDialog from '../EditVolumeDialog'
import EditVolumeMetadataModal from '../EditVolumeMetadataModal'
import ExtendVolumeDialog from '../ExtendVolumeDialog'
import RetypeVolumeDialog from '../RetypeVolumeDialog'
import { singleVolumeSelector, volumesSelector } from '../selectors'
import SnapshotVolumeDialog from '../SnapshotVolumeDialog'
import { VOLUME_ACTION_TOOLTIPS } from '../tooltipConstants'
import TransferVolumeDialog from '../TransferVolumeDialog'
import UploadVolumeAsImageDialog from '../UploadVolumeAsImageDialog'
import { IVolumeDetailsPageTabs } from './model'
import Overview from './Overview'

type SelectorModel = ArrayElement<ReturnType<typeof volumesSelector>>

const useStyles = makeStyles<Theme>((theme) => ({
  grid: {
    display: 'grid',
    gap: 32,
  },
  actions: {
    display: 'grid',
    gridAutoColumns: 'auto max-content',
    gridAutoFlow: 'column',
    gap: 16,
  },
  dropdownAction: {
    background: theme.components.dropdown.background,
    border: 'none',
    '& .button-text': {
      color: theme.components.dropdown.color,
      justifyContent: 'flex-start',
    },
  },
}))

const VolumeDetailsPage = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { match } = useReactRouter()
  const { id } = match.params
  const [allTenantVolume, setAllTenantVolume] = useState(false)
  const [blueprintDetails, setBlueprintDetails] = useState({})

  const { loading, reload } = useListAction(listSingleVolume, { params: { id } })
  const { update, updating } = useUpdateAction(updateSingleVolume)
  const data = useSelector(singleVolumeSelector)
  const volume = useMemo(() => {
    return data[0]
  }, [data])

  const loadingProperVolume = volume?.id !== id

  // Update the volume of singleVolume
  useEffect(() => {
    update({ id })
  }, [id])

  useEffect(() => {
    dispatch(
      clientActions.updateBreadcrumbParams({
        id: volume?.name || id,
      }),
    )
    return () => {
      dispatch(clientActions.resetBreadcrumbParams())
    }
  }, [volume?.name, id])

  useEffect(() => {
    const getDriverDetails = async () => {
      const blueprints = await getBlueprints()
      setBlueprintDetails(blueprints[0]?.storageBackends)
    }

    getDriverDetails()
  }, [])

  const dropdownBatchActions: GridBatchActionGroupProps<SelectorModel>[] = useMemo(
    () => [
      {
        icon: '',
        label: 'Actions',
        actions: [
          {
            key: 'Edit',
            label: 'Edit',
            icon: 'edit',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              EditVolumeDialog,
              { rows: [volume], reloadVolumes: () => reload(true) },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'Snapshot',
            label: 'Snapshot',
            icon: 'camera',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              SnapshotVolumeDialog,
              { rows: [volume] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'EditMetadata',
            label: 'Edit Metadata',
            icon: 'tags',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              EditVolumeMetadataModal,
              { rows: [volume], reloadVolumes: () => reload(true) },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'Upload',
            label: 'Upload as Image',
            icon: 'upload',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              UploadVolumeAsImageDialog,
              { rows: [volume], reloadVolumes: () => reload(true) },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'Attach',
            label: 'Attach Volume',
            icon: 'plus-square',
            disabled: volume?.status !== 'available',
            tooltip:
              volume?.status !== 'available' ? VOLUME_ACTION_TOOLTIPS.ATTACH_AVAILABLE_STATUS : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              AttachVolumeDialog,
              { rows: [volume], reloadVolumes: () => reload(true) },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'Detach',
            label: 'Detach Volume',
            icon: 'minus-square',
            disabled: volume?.status !== 'in-use',
            tooltip: volume?.status !== 'in-use' ? VOLUME_ACTION_TOOLTIPS.DETACH_IN_USE_STATUS : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              DetachVolumeDialog,
              { rows: [volume], reloadVolumes: () => reload(true) },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'Extend',
            label: 'Extend Volume',
            icon: 'square-up',
            disabled: ((vol) => {
              const volumeHostAtt = vol?.['os-vol-host-attr:host'] || ''
              const volumeType = volumeHostAtt?.split('#')?.[1]
              const volumeTypeConfig = volumeHostAtt?.split('@')?.[1]?.split('#')?.[0]
              const volumeDriver = blueprintDetails?.[volumeType]?.[volumeTypeConfig]?.driver || ''
              return (
                vol?.status !== 'available' && (vol?.status !== 'in-use' || volumeDriver === 'NFS')
              )
            })(volume),
            tooltip: ((vol) => {
              const volumeHostAtt = vol?.['os-vol-host-attr:host'] || ''
              const volumeType = volumeHostAtt?.split('#')?.[1]
              const volumeTypeConfig = volumeHostAtt?.split('@')?.[1]?.split('#')?.[0]
              const volumeDriver = blueprintDetails?.[volumeType]?.[volumeTypeConfig]?.driver || ''
              return vol?.status !== 'available' &&
                (vol?.status !== 'in-use' || volumeDriver === 'NFS')
                ? VOLUME_ACTION_TOOLTIPS.EXTEND_VOLUME_CONDITION
                : ''
            })(volume),
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              ExtendVolumeDialog,
              { rows: [volume], reloadVolumes: () => reload(true) },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'Retype',
            label: 'Retype Volume',
            icon: 'swap',
            disabled: volume?.status !== 'available',
            tooltip:
              volume?.status !== 'available' ? VOLUME_ACTION_TOOLTIPS.RETYPE_AVAILABLE_STATUS : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              RetypeVolumeDialog,
              { rows: [volume], reloadVolumes: () => reload(true) },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'Transfer',
            label: 'Transfer Volume',
            icon: 'arrow-right-arrow-left',
            disabled: volume?.status !== 'available' || allTenantVolume,
            tooltip:
              volume?.status !== 'available' || allTenantVolume
                ? VOLUME_ACTION_TOOLTIPS.TRANSFER_AVAILABLE_STATUS
                : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              TransferVolumeDialog,
              { rows: [volume], reloadVolumes: () => reload(true) },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'CancelTransfer',
            label: 'Cancel Transfer',
            icon: 'ban',
            disabled: volume?.status !== 'awaiting-transfer' || allTenantVolume,
            tooltip:
              volume?.status !== 'awaiting-transfer' || allTenantVolume
                ? VOLUME_ACTION_TOOLTIPS.CANCEL_TRANSFER_AWAITING_STATUS
                : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              CancelTransferDialog,
              { rows: [volume], reloadVolumes: () => reload(true) },
              {
                className: classes.dropdownAction,
              },
            ),
          },
        ],
      },
    ],
    [classes, blueprintDetails, allTenantVolume, volume],
  )

  return (
    <>
      <DocumentMeta title="Volume Details" breadcrumbs />
      <HeaderRefreshPortal>
        <Button
          icon="refresh"
          variant="secondary"
          onClick={() => {
            reload(true)
          }}
          loading={loading || loadingProperVolume || updating}
        >
          Refresh
        </Button>
      </HeaderRefreshPortal>
      {!isReadonlyUser() && (
        <HeaderPrimaryActionPortal>
          <>
            {dropdownBatchActions.map(({ label, actions }) => (
              <BatchActionDropdown<SelectorModel>
                renderOutsideGrid
                icon=""
                key={label}
                label={label}
                actions={actions}
              />
            ))}
          </>
        </HeaderPrimaryActionPortal>
      )}
      <Tabs route={routeManager.routes.openstack.volumeDetails}>
        <Tab label="Overview" value={IVolumeDetailsPageTabs.Overview}>
          <Overview volume={volume || []} loading={loading || loadingProperVolume || updating} />
        </Tab>
      </Tabs>
    </>
  )
}

export default VolumeDetailsPage
